import * as React from "react";
import { HealthCheckSpec } from "../../../../../schema/types/workload/entity";
import { ProbeInput } from "../../../../../schema/types/workload/input";
import { useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { PortInput } from "../../../../../schema/types/workload/input";

interface UseDefaultProbesReturnValue {
  readiness: HealthCheckSpec | null;
  liveness: HealthCheckSpec | null;
}

function probeInputToSpec(input: ProbeInput): HealthCheckSpec | null {
  const p: HealthCheckSpec = {
    failureThreshold: Number(input.failureThreshold),
    successThreshold: Number(input.successThreshold),
    initialDelaySeconds: Number(input.initialDelaySeconds),
    periodSeconds: Number(input.periodSeconds),
    timeoutSeconds: Number(input.timeoutSeconds),
  };

  if (input.type === "exec") {
    p.exec = {
      command: [input.exec.command, ...input.exec.args.map((arg) => arg.value)],
    };
  }
  if (input.type === "grpc" && input.grpc.port.length > 0) {
    p.grpc = { port: Number(input.grpc.port) };
  }
  if (input.type === "tcp" && input.tcpSocket.port.length > 0) {
    p.tcpSocket = { port: Number(input.tcpSocket.port) };
  }
  if (input.type === "http") {
    p.httpGet = {
      path: input.httpGet.path || "/",
      scheme: input.httpGet.scheme,
    };
    if (input.httpGet.port) {
      p.httpGet.port = Number(input.httpGet.port);
    }
    if (input.httpGet.httpHeaders.length > 0) {
      p.httpGet.httpHeaders = input.httpGet.httpHeaders.map((h) => ({ name: h.name, value: h.value }));
    }
  }

  return p;
}

export function useDefaultProbes(index: number): UseDefaultProbesReturnValue {
  const type = useCpFormValue("type");
  const ports: PortInput[] = useCpFormValue(`containers.${index}.ports`) || [];
  const readinessAlreadySet: boolean = useCpFormValue(`containers.${index}.readinessProbe.alreadySet`);
  const readinessOverride: boolean = useCpFormValue(`containers.${index}.readinessProbe.override`);
  const livenessAlreadySet: boolean = useCpFormValue(`containers.${index}.livenessProbe.alreadySet`);
  const livenessOverride: boolean = useCpFormValue(`containers.${index}.livenessProbe.override`);
  const readinessProbeInput: ProbeInput | undefined = useCpFormValue(`containers.${index}.readinessProbe`);

  const defaults = React.useMemo(() => {
    const result: UseDefaultProbesReturnValue = {
      readiness: null,
      liveness: null,
    };

    // Serverless default: TCP probe on the first container port
    if (
      type === "serverless" &&
      !readinessAlreadySet &&
      !readinessOverride &&
      ports.length > 0 &&
      ports[0].number
    ) {
      result.readiness = {
        tcpSocket: { port: Number(ports[0].number) },
        initialDelaySeconds: 0,
        periodSeconds: 10,
        timeoutSeconds: 1,
        successThreshold: 1,
        failureThreshold: 3,
      };
    }

    // Liveness default: if readiness is set and liveness is not, readiness is used for both
    if (!livenessAlreadySet && !livenessOverride) {
      if (result.readiness) {
        // Clone the serverless readiness default with a longer initial delay
        result.liveness = { ...result.readiness, initialDelaySeconds: 60 };
        if (result.readiness.tcpSocket) {
          result.liveness.tcpSocket = { ...result.readiness.tcpSocket };
        }
      } else if ((readinessAlreadySet || readinessOverride) && readinessProbeInput) {
        // Convert the user-configured readiness probe to a spec for liveness default
        const spec = probeInputToSpec(readinessProbeInput);
        if (spec) {
          spec.initialDelaySeconds = 60;
          result.liveness = spec;
        }
      }
    }

    return result;
  }, [type, ports, readinessAlreadySet, readinessOverride, livenessAlreadySet, livenessOverride, readinessProbeInput]);

  return defaults;
}
